import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Star,
  StarOff,
  Eye,
  EyeOff,
  Search,
  RefreshCw,
  Loader2,
  FileText,
  Pencil,
  Save,
  Plus,
  Trash2,
  GripVertical,
  Settings,
  Filter,
  CheckSquare,
  Square,
  MoreHorizontal
} from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE_URL } from '@/lib/apiBase';

const API = API_BASE_URL;

const FieldsPage = () => {
  const { locationId } = useParams();
  const { isAdmin } = useAuth();
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [updating, setUpdating] = useState({});
  
  // Filter state
  const [filter, setFilter] = useState('all'); // all, starred, visible, hidden
  
  // Bulk selection state
  const [selectedFields, setSelectedFields] = useState(new Set());
  const [bulkUpdating, setBulkUpdating] = useState(false);
  
  // Edit field value state
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Manage options state
  const [managingField, setManagingField] = useState(null);
  const [editedOptions, setEditedOptions] = useState([]);
  const [newOptionName, setNewOptionName] = useState('');
  const [newOptionValue, setNewOptionValue] = useState('');
  const [savingOptions, setSavingOptions] = useState(false);
  
  // Syncing options to CRM state
  const [syncingOptions, setSyncingOptions] = useState(false);

  const fetchFields = async () => {
    try {
      const response = await axios.get(`${API}/locations/${locationId}/fields`);
      setFields(response.data);
      setSelectedFields(new Set()); // Clear selection on refresh
    } catch (error) {
      console.error('Failed to fetch fields:', error);
      toast.error('Failed to load fields');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFields();
  }, [locationId]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/locations/${locationId}/sync-fields`);
      toast.success(response.data.message);
      fetchFields();
    } catch (error) {
      toast.error('Failed to sync fields');
    } finally {
      setSyncing(false);
    }
  };

  const handleToggle = async (fieldId, type, currentValue) => {
    setUpdating(prev => ({ ...prev, [fieldId]: type }));
    try {
      await axios.patch(`${API}/fields/${fieldId}/visibility`, {
        [type]: !currentValue
      });
      setFields(prev => prev.map(f => 
        f.id === fieldId ? { ...f, [type]: !currentValue } : f
      ));
      toast.success(`Field ${type === 'is_visible' ? 'visibility' : 'star'} updated`);
    } catch (error) {
      toast.error('Failed to update field');
    } finally {
      setUpdating(prev => ({ ...prev, [fieldId]: null }));
    }
  };

  // Check if field is dropdown type
  const isDropdownField = (field) => {
    const type = field?.field_type?.toLowerCase() || '';
    return type.includes('dropdown') || 
           type.includes('single_option') || 
           type.includes('multiple_option') ||
           type.includes('options') ||
           type.includes('select');
  };

  // Get options for a field (handles both array of strings and array of objects)
  const getFieldOptions = (field) => {
    if (!field?.options) return [];
    return field.options.map(opt => {
      if (typeof opt === 'string') {
        return { name: opt, value: opt };
      }
      return { name: opt.name || opt.value, value: opt.value || opt.name };
    });
  };

  // Open edit value dialog
  const openEditDialog = (field) => {
    setEditingField(field);
    setEditValue(field.current_value || '');
  };

  // Save field value
  const handleSaveField = async () => {
    if (!editingField) return;
    setSaving(true);
    
    try {
      await axios.put(`${API}/fields/${editingField.id}/value`, {
        value: editValue
      });
      
      setFields(prev => prev.map(f => 
        f.id === editingField.id ? { ...f, current_value: editValue } : f
      ));
      
      toast.success('Field value updated');
      setEditingField(null);
      setEditValue('');
    } catch (error) {
      toast.error('Failed to update field value');
    } finally {
      setSaving(false);
    }
  };

  // Open manage options dialog
  const openManageOptions = (field) => {
    setManagingField(field);
    const options = getFieldOptions(field);
    setEditedOptions(options);
    setNewOptionName('');
    setNewOptionValue('');
  };

  // Add new option
  const handleAddOption = () => {
    if (!newOptionName.trim()) return;
    
    const value = newOptionValue.trim() || newOptionName.trim();
    
    if (editedOptions.some(opt => opt.name === newOptionName.trim())) {
      toast.error('Option name already exists');
      return;
    }
    
    setEditedOptions([...editedOptions, { name: newOptionName.trim(), value }]);
    setNewOptionName('');
    setNewOptionValue('');
  };

  // Remove option
  const handleRemoveOption = (index) => {
    setEditedOptions(editedOptions.filter((_, i) => i !== index));
  };

  // Update option
  const handleUpdateOption = (index, field, value) => {
    const updated = [...editedOptions];
    updated[index] = { ...updated[index], [field]: value };
    setEditedOptions(updated);
  };

  // Calculate changes between original and edited options
  const calculateChanges = (originalOptions, newOptions) => {
    const changes = [];
    const originalMap = new Map(originalOptions.map(o => [o.name, o]));
    const newMap = new Map(newOptions.map(o => [o.name, o]));
    
    // Find added options
    newOptions.forEach(opt => {
      if (!originalMap.has(opt.name)) {
        changes.push({ action: 'added', option_name: opt.name, new_value: opt.value });
      }
    });
    
    // Find deleted options
    originalOptions.forEach(opt => {
      if (!newMap.has(opt.name)) {
        changes.push({ action: 'deleted', option_name: opt.name, old_value: opt.value });
      }
    });
    
    // Find updated options (same name, different value)
    newOptions.forEach(opt => {
      const original = originalMap.get(opt.name);
      if (original && original.value !== opt.value) {
        changes.push({ 
          action: 'updated', 
          option_name: opt.name, 
          old_value: original.value, 
          new_value: opt.value 
        });
      }
    });
    
    return changes;
  };

  // Save options and create sync request
  const handleSaveOptions = async () => {
    if (!managingField) return;
    setSavingOptions(true);
    
    try {
      const validOptions = editedOptions.filter(opt => opt.name.trim() !== '');
      const originalOptions = getFieldOptions(managingField);
      
      // Calculate what changed
      const changes = calculateChanges(originalOptions, validOptions);
      
      // Save options locally
      await axios.patch(`${API}/fields/${managingField.id}/options`, {
        options: validOptions
      });
      
      setFields(prev => prev.map(f => 
        f.id === managingField.id ? { ...f, options: validOptions } : f
      ));
      
      // Create sync request if there are changes
      if (changes.length > 0) {
        try {
          await axios.post(`${API}/sync-requests`, {
            field_id: managingField.id,
            changes: changes
          });
          toast.success('Options saved! Sync request submitted to support team.', { duration: 5000 });
        } catch (syncError) {
          console.error('Failed to create sync request:', syncError);
          toast.success('Options saved locally');
          toast.info('Could not create sync request. Please notify support manually.', { duration: 5000 });
        }
      } else {
        toast.success('Options saved (no changes detected)');
      }
      
      setManagingField(null);
      setEditedOptions([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update options');
    } finally {
      setSavingOptions(false);
    }
  };

  // Bulk selection handlers
  const toggleFieldSelection = (fieldId) => {
    setSelectedFields(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fieldId)) {
        newSet.delete(fieldId);
      } else {
        newSet.add(fieldId);
      }
      return newSet;
    });
  };

  const selectAllVisible = () => {
    const visibleIds = filteredFields.filter(f => f.is_visible).map(f => f.id);
    setSelectedFields(new Set(visibleIds));
  };

  const selectAllHidden = () => {
    const hiddenIds = filteredFields.filter(f => !f.is_visible).map(f => f.id);
    setSelectedFields(new Set(hiddenIds));
  };

  const selectAll = () => {
    setSelectedFields(new Set(filteredFields.map(f => f.id)));
  };

  const clearSelection = () => {
    setSelectedFields(new Set());
  };

  const handleBulkVisibility = async (visible) => {
    if (selectedFields.size === 0) return;
    setBulkUpdating(true);
    
    try {
      await axios.post(`${API}/fields/bulk-visibility`, {
        field_ids: Array.from(selectedFields),
        is_visible: visible
      });
      
      setFields(prev => prev.map(f => 
        selectedFields.has(f.id) ? { ...f, is_visible: visible } : f
      ));
      
      toast.success(`${selectedFields.size} fields ${visible ? 'shown' : 'hidden'}`);
      setSelectedFields(new Set());
    } catch (error) {
      toast.error('Failed to update fields');
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleBulkStar = async (starred) => {
    if (selectedFields.size === 0) return;
    setBulkUpdating(true);
    
    try {
      await axios.post(`${API}/fields/bulk-star`, {
        field_ids: Array.from(selectedFields),
        is_starred: starred
      });
      
      setFields(prev => prev.map(f => 
        selectedFields.has(f.id) ? { ...f, is_starred: starred } : f
      ));
      
      toast.success(`${selectedFields.size} fields ${starred ? 'starred' : 'unstarred'}`);
      setSelectedFields(new Set());
    } catch (error) {
      toast.error('Failed to update fields');
    } finally {
      setBulkUpdating(false);
    }
  };

  // Filtered fields with search and filter state
  const filteredFields = useMemo(() => {
    return fields.filter(field => {
      // Search filter
      const matchesSearch = field.field_name.toLowerCase().includes(search.toLowerCase()) ||
        field.field_type.toLowerCase().includes(search.toLowerCase());
      
      if (!matchesSearch) return false;
      
      // Status filter
      switch (filter) {
        case 'starred':
          return field.is_starred;
        case 'visible':
          return field.is_visible;
        case 'hidden':
          return !field.is_visible;
        default:
          return true;
      }
    });
  }, [fields, search, filter]);

  const getFieldTypeBadge = (type) => {
    const lowerType = type?.toLowerCase() || '';
    if (lowerType.includes('dropdown') || lowerType.includes('single_option')) {
      return 'bg-purple-50 text-purple-700 border-purple-200';
    }
    if (lowerType.includes('text')) {
      return 'bg-blue-50 text-blue-700 border-blue-200';
    }
    if (lowerType.includes('textarea') || lowerType.includes('multi')) {
      return 'bg-green-50 text-green-700 border-green-200';
    }
    if (lowerType.includes('number')) {
      return 'bg-amber-50 text-amber-700 border-amber-200';
    }
    if (lowerType.includes('date')) {
      return 'bg-rose-50 text-rose-700 border-rose-200';
    }
    return 'bg-slate-50 text-slate-700 border-slate-200';
  };

  const getFieldGroupBadge = (group) => {
    if (group === 'opportunity') {
      return 'bg-orange-50 text-orange-700 border-orange-200';
    }
    return 'bg-teal-50 text-teal-700 border-teal-200';
  };

  const getDisplayFieldType = (type) => {
    const lowerType = type?.toLowerCase() || '';
    if (lowerType.includes('single_option')) return 'Dropdown (Single)';
    if (lowerType.includes('multi_option')) return 'Dropdown (Multi)';
    if (lowerType.includes('dropdown')) return 'Dropdown';
    return type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="fields-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Link to="/locations" className="hover:text-slate-700">Locations</Link>
        <span>/</span>
        <Link to={`/locations/${locationId}`} className="hover:text-slate-700">Location</Link>
        <span>/</span>
        <span className="text-slate-900 font-medium">Fields</span>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Custom Fields
          </h1>
          <p className="text-slate-500 mt-1">
            Manage field visibility, values, and dropdown options
          </p>
        </div>
        {isAdmin() && (
          <Button 
            variant="outline" 
            onClick={handleSync}
            disabled={syncing}
            data-testid="sync-fields-btn"
          >
            {syncing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Sync from CRM
          </Button>
        )}
      </div>

      {/* Search and Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search fields..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-fields"
          />
        </div>
        
        {/* Filter Dropdown */}
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[180px]" data-testid="filter-select">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Fields</SelectItem>
            <SelectItem value="starred">Starred Only</SelectItem>
            <SelectItem value="visible">Visible Only</SelectItem>
            <SelectItem value="hidden">Hidden Only</SelectItem>
          </SelectContent>
        </Select>
        
        {/* Bulk Actions */}
        {isAdmin() && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="bulk-actions-btn">
                <CheckSquare className="w-4 h-4 mr-2" />
                Bulk Actions
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={selectAll}>
                Select All ({filteredFields.length})
              </DropdownMenuItem>
              <DropdownMenuItem onClick={selectAllVisible}>
                Select All Visible
              </DropdownMenuItem>
              <DropdownMenuItem onClick={selectAllHidden}>
                Select All Hidden
              </DropdownMenuItem>
              <DropdownMenuItem onClick={clearSelection}>
                Clear Selection
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => handleBulkVisibility(true)}
                disabled={selectedFields.size === 0 || bulkUpdating}
              >
                <Eye className="w-4 h-4 mr-2" />
                Show Selected ({selectedFields.size})
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleBulkVisibility(false)}
                disabled={selectedFields.size === 0 || bulkUpdating}
              >
                <EyeOff className="w-4 h-4 mr-2" />
                Hide Selected ({selectedFields.size})
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => handleBulkStar(true)}
                disabled={selectedFields.size === 0 || bulkUpdating}
              >
                <Star className="w-4 h-4 mr-2" />
                Star Selected
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleBulkStar(false)}
                disabled={selectedFields.size === 0 || bulkUpdating}
              >
                <StarOff className="w-4 h-4 mr-2" />
                Unstar Selected
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Selection Info Bar */}
      {selectedFields.size > 0 && (
        <div className="flex items-center justify-between bg-indigo-50 rounded-lg px-4 py-2">
          <span className="text-sm text-indigo-700 font-medium">
            {selectedFields.size} field{selectedFields.size !== 1 ? 's' : ''} selected
          </span>
          <div className="flex gap-2">
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => handleBulkVisibility(true)}
              disabled={bulkUpdating}
              className="h-7"
            >
              <Eye className="w-3 h-3 mr-1" />
              Show
            </Button>
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => handleBulkVisibility(false)}
              disabled={bulkUpdating}
              className="h-7"
            >
              <EyeOff className="w-3 h-3 mr-1" />
              Hide
            </Button>
            <Button 
              size="sm" 
              variant="ghost"
              onClick={clearSelection}
              className="h-7"
            >
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Fields List */}
      {filteredFields.length === 0 ? (
        <Card className="border-dashed border-2 border-slate-200">
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {search || filter !== 'all' ? 'No fields match your criteria' : 'No fields synced'}
            </h3>
            <p className="text-slate-500 mb-6">
              {search || filter !== 'all' ? 'Try adjusting your search or filter' : 'Click "Sync from CRM" to pull custom fields'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredFields.map((field) => (
            <Card key={field.id} className={`overflow-hidden ${selectedFields.has(field.id) ? 'ring-2 ring-indigo-500' : ''}`} data-testid={`field-card-${field.id}`}>
              <div className="p-4 flex items-start justify-between gap-4">
                {/* Checkbox for selection */}
                {isAdmin() && (
                  <Checkbox
                    checked={selectedFields.has(field.id)}
                    onCheckedChange={() => toggleFieldSelection(field.id)}
                    className="mt-1"
                    data-testid={`select-field-${field.id}`}
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-slate-900">
                      {field.field_name}
                    </span>
                    {field.is_starred && (
                      <Star className="w-4 h-4 text-amber-500 fill-amber-500 flex-shrink-0" />
                    )}
                    <Badge variant="outline" className={getFieldTypeBadge(field.field_type)}>
                      {getDisplayFieldType(field.field_type)}
                    </Badge>
                    {field.field_group && (
                      <Badge variant="outline" className={getFieldGroupBadge(field.field_group)}>
                        {field.field_group === 'opportunity' ? 'Opportunity' : 'Contact'}
                      </Badge>
                    )}
                    {field.current_value && (
                      <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded">
                        Current: {field.current_value}
                      </span>
                    )}
                  </div>
                  
                  {/* Dropdown Options Display */}
                  {isDropdownField(field) && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-600">
                          Dropdown Options ({getFieldOptions(field).length})
                        </span>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openManageOptions(field)}
                            className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 h-7"
                            data-testid={`manage-options-${field.id}`}
                          >
                            <Settings className="w-3 h-3 mr-1" />
                            Manage Options
                          </Button>
                        </div>
                      </div>
                      {getFieldOptions(field).length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {getFieldOptions(field).map((opt, i) => (
                            <span 
                              key={opt.id || opt.name || `opt-${i}`}
                              className="text-sm bg-slate-100 text-slate-700 px-3 py-1 rounded-full border border-slate-200"
                            >
                              {opt.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-400 italic">No options defined</p>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {isAdmin() && (
                    <>
                      {/* Star Toggle */}
                      <button
                        onClick={() => handleToggle(field.id, 'is_starred', field.is_starred)}
                        disabled={updating[field.id] === 'is_starred'}
                        className="p-2 hover:bg-slate-100 rounded-md transition-colors"
                        title={field.is_starred ? 'Unstar field' : 'Star field'}
                        data-testid={`star-field-${field.id}`}
                      >
                        {updating[field.id] === 'is_starred' ? (
                          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                        ) : field.is_starred ? (
                          <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                        ) : (
                          <StarOff className="w-5 h-5 text-slate-400" />
                        )}
                      </button>

                      {/* Visibility Toggle */}
                      <div className="flex items-center gap-2">
                        {field.is_visible ? (
                          <Eye className="w-4 h-4 text-slate-500" />
                        ) : (
                          <EyeOff className="w-4 h-4 text-slate-400" />
                        )}
                        <Switch
                          checked={field.is_visible}
                          onCheckedChange={() => handleToggle(field.id, 'is_visible', field.is_visible)}
                          disabled={updating[field.id] === 'is_visible'}
                          data-testid={`toggle-visibility-${field.id}`}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Legend */}
      {isAdmin() && fields.length > 0 && (
        <div className="flex items-center gap-6 text-sm text-slate-500">
          <div className="flex items-center gap-2">
            <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
            <span>Starred fields appear first for users</span>
          </div>
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            <span>Toggle controls user visibility</span>
          </div>
        </div>
      )}

      {/* Manage Dropdown Options Dialog */}
      <Dialog open={!!managingField} onOpenChange={() => setManagingField(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Edit Custom Field: {managingField?.field_name}
            </DialogTitle>
            <DialogDescription>
              Add, edit, or remove dropdown options
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4 space-y-4">
            {/* Add New Option */}
            <div className="grid grid-cols-[1fr_1fr_auto] gap-2">
              <Input
                placeholder="Option Name"
                value={newOptionName}
                onChange={(e) => {
                  const name = e.target.value;
                  setNewOptionName(name);
                  // Auto-fill Option Value with the same value
                  setNewOptionValue(name);
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleAddOption()}
                data-testid="new-option-name"
              />
              <Input
                placeholder="Option Value (auto-filled)"
                value={newOptionValue}
                onChange={(e) => setNewOptionValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddOption()}
                data-testid="new-option-value"
              />
              <Button 
                onClick={handleAddOption}
                disabled={!newOptionName.trim()}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="add-option-btn"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            {/* Options Table */}
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-10"></TableHead>
                    <TableHead className="font-semibold text-slate-600">OPTION NAME</TableHead>
                    <TableHead className="font-semibold text-slate-600">OPTION VALUE</TableHead>
                    <TableHead className="w-20 text-center font-semibold text-slate-600">ACTION</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {editedOptions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-slate-400">
                        No options yet. Add some above.
                      </TableCell>
                    </TableRow>
                  ) : (
                    editedOptions.map((opt, index) => (
                      <TableRow key={opt.id || opt.name || `option-${index}`} data-testid={`option-row-${index}`}>
                        <TableCell className="w-10">
                          <GripVertical className="w-4 h-4 text-slate-400 cursor-move" />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={opt.name}
                            onChange={(e) => {
                              const name = e.target.value;
                              // Update both name and value together
                              const updated = [...editedOptions];
                              updated[index] = { ...updated[index], name: name, value: name };
                              setEditedOptions(updated);
                            }}
                            className="bg-white"
                            data-testid={`option-name-${index}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={opt.value}
                            onChange={(e) => handleUpdateOption(index, 'value', e.target.value)}
                            className="bg-white"
                            data-testid={`option-value-${index}`}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveOption(index)}
                            className="text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                            data-testid={`delete-option-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setManagingField(null)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveOptions}
              disabled={savingOptions}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-options-btn"
            >
              {savingOptions ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FieldsPage;
